import {
    DELETE_DEAL_TEMPLATE,
    DELETE_DEAL_TEMPLATE_FAILURE,
    DELETE_DEAL_TEMPLATE_REQUEST,
    DELETE_DEAL_TEMPLATE_SUCCESS,
    DROP_DELETE_DEAL_TEMPLATE,
    DROP_UPDATE_DEAL_TEMPLATE,
    GET_DEAL_TEMPLATES,
    GET_DEAL_TEMPLATES_FAILURE,
    GET_DEAL_TEMPLATES_REQUEST,
    GET_DEAL_TEMPLATES_SUCCESS,
    GET_MANUAL_DEAL_TEMPLATES,
    GET_MANUAL_DEAL_TEMPLATES_FAILURE,
    GET_MANUAL_DEAL_TEMPLATES_REQUEST,
    GET_MANUAL_DEAL_TEMPLATES_SUCCESS,
    INIT_TEMPLATE_FROM_DEAL_FAILURE,
    INIT_TEMPLATE_FROM_DEAL_REQUEST,
    INIT_TEMPLATE_FROM_DEAL_SUCCESS,
    SAVE_DEAL_TEMPLATE,
    SAVE_DEAL_TEMPLATE_FAILURE,
    SAVE_DEAL_TEMPLATE_REQUEST,
    SAVE_DEAL_TEMPLATE_SUCCESS,
    TOGGLE_SAVE_PAYMENT_TERMS,
    UPDATE_DEAL_TEMPLATE,
    UPDATE_DEAL_TEMPLATE_REQUEST,
    UPDATE_DEAL_TEMPLATE_SUCCESS,
    UPDATE_DEAL_TEMPLATE_FAILURE,
    TRIGGER_TEMPLATE_BUILDER_MODE,
    MANUALLY_APPLY_DEAL_TEMPLATE_REQUEST,
    REVERT_MANUALLY_APPLIED_TEMPLATE
} from '../actionTypes/dealTemplateActionTypes';
import { buildApplyTemplateFromDealPayload } from '../utils/offerPayloadUtil';
import type {
    DraftDealTemplate,
    DataToUpdateTemplate,
    StateTree,
    DataToRemoveTemplate,
    DealTemplateInventoryTypeExtended,
    DealTemplateInventoryType,
    ManualTemplateApplySource,
    ParamsToSaveTemplate
} from '@makemydeal/dr-dash-types';
import { locHrefUtil, apiConfigHelper, MANAGER_VIEW_APP_PREFIX } from '@makemydeal/dr-shared-ui-utils';
import type { RSAARequestType, RSAASuccessType, RSAAFailureType } from 'redux-api-middleware';
import { RSAA } from 'redux-api-middleware';
import type { IConfigObj } from '@makemydeal/dr-common-utils';
import { buildFromConfig } from '@makemydeal/dr-common-utils';
import type { RSAACallRequest } from '@makemydeal/dr-react-18-wrappers';
import type { OfferType } from '@makemydeal/dr-platform-types';
import type { ISaveOffer, TemplateDetails } from '@makemydeal/dr-dash-bff-types';

type BridgeUserData = {
    bridgeId: string;
    name: string;
};

export type TemplateSavePayload = Record<OfferType, ISaveOffer> & {
    user: BridgeUserData;
    savePaymentTerms: boolean;
    activeDealType: OfferType;
    template?: TemplateDetails;
};

export type SaveTemplateRSAAOptions = {
    state: StateTree;
    templateSavePayload: TemplateSavePayload;
    source?: string;
};

export const saveDealTemplateSuccess = (template: DraftDealTemplate) => ({
    type: SAVE_DEAL_TEMPLATE_SUCCESS,
    payload: template
});

export const saveTemplate = (payload?: ParamsToSaveTemplate) => {
    return {
        type: SAVE_DEAL_TEMPLATE,
        payload
    };
};

export const getDealTemplates = () => {
    return {
        type: GET_DEAL_TEMPLATES
    };
};

export const getManualDealTemplates = (params?: Record<string, string>) => {
    return {
        type: GET_MANUAL_DEAL_TEMPLATES,
        payload: { queryParams: params }
    };
};

export const removeDealTemplate = (template: DataToUpdateTemplate) => {
    return {
        type: DELETE_DEAL_TEMPLATE,
        payload: { template }
    };
};

type UpdateDealTemplateMeta = {
    name: string;
    id: string;
    inventoryType?: DealTemplateInventoryType;
};

export const updateDealTemplate = (template: DataToUpdateTemplate, meta: UpdateDealTemplateMeta) => {
    return {
        type: UPDATE_DEAL_TEMPLATE,
        payload: { template, meta }
    };
};

export const dropRemoveDealTemplateData = () => {
    return {
        type: DROP_DELETE_DEAL_TEMPLATE
    };
};

export const dropUpdateDealTemplateData = () => {
    return {
        type: DROP_UPDATE_DEAL_TEMPLATE
    };
};

export const triggerTemplateBuilderMode = (show: boolean, builderModeTab?: 'manage-existing-templates' | 'save-template') => {
    return {
        type: TRIGGER_TEMPLATE_BUILDER_MODE,
        payload: { show, builderModeTab }
    };
};

export const toggleSavePaymentTerms = (checked: boolean) => ({
    type: TOGGLE_SAVE_PAYMENT_TERMS,
    payload: { savePaymentTerms: checked }
});

export const saveTemplateRSAA = (options: SaveTemplateRSAAOptions) => {
    const { templateSavePayload, source } = options;
    const locHref = locHrefUtil.getLocHref();
    const bffApiUrlResult = apiConfigHelper.buildBffApiUrl(MANAGER_VIEW_APP_PREFIX, locHref, 'template/save');
    const endpoint = bffApiUrlResult.url;

    return {
        [RSAA]: {
            endpoint,
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(templateSavePayload),
            types: [
                { type: SAVE_DEAL_TEMPLATE_REQUEST },
                {
                    type: SAVE_DEAL_TEMPLATE_SUCCESS,
                    meta: { source }
                },
                {
                    type: SAVE_DEAL_TEMPLATE_FAILURE,
                    meta: { source }
                }
            ]
        }
    };
};

export const getGeneralTemplates = (
    endpoint: string,
    actionTypes: [RSAARequestType<any, any, any>, RSAASuccessType<any, any, any>, RSAAFailureType<any, any, any>],
    queryParams?: Record<string, string>
) => {
    const url = new URL(endpoint);

    if (queryParams) {
        Object.entries(queryParams).forEach(([key, value]) => {
            url.searchParams.append(key, value);
        });
    }

    return {
        [RSAA]: {
            endpoint: url.toString(),
            method: 'GET',
            headers: { 'Content-Type': 'application/json' },
            types: actionTypes,
            // Overriding the fetch function to ensure compatibility with the module's context.
            // When working with a federated module, the global `fetch` instance (window.fetch) may differ from the host application's instance.
            // This can cause issues with interceptors
            fetch: async (...args) => {
                return await fetch(...args);
            }
        } satisfies RSAACallRequest
    };
};

export const getTemplatesRSAA = (
    dealerId: number,
    bffEndpointConfig: IConfigObj,
    params?: {
        [key: string]: string;
    }
) => {
    const proxyUrl = buildFromConfig(bffEndpointConfig, '');
    const dealerIdEscaped = encodeURIComponent(dealerId);
    const endpoint = `${proxyUrl}/dealer/${dealerIdEscaped}/deal-templates`;

    return getGeneralTemplates(
        endpoint,
        [GET_DEAL_TEMPLATES_REQUEST, GET_DEAL_TEMPLATES_SUCCESS, GET_DEAL_TEMPLATES_FAILURE],
        params
    );
};
export const getManualTemplatesRSAA = (dealerId: number, bffEndpointConfig: IConfigObj, params?: Record<string, string>) => {
    const proxyUrl = buildFromConfig(bffEndpointConfig, '');
    const dealerIdEscaped = encodeURIComponent(dealerId);
    const endpoint = `${proxyUrl}/dealer/${dealerIdEscaped}/deal-templates`;

    return getGeneralTemplates(
        endpoint,
        [GET_MANUAL_DEAL_TEMPLATES_REQUEST, GET_MANUAL_DEAL_TEMPLATES_SUCCESS, GET_MANUAL_DEAL_TEMPLATES_FAILURE],
        {
            ...params,
            isManualApply: 'true'
        }
    );
};

export const deleteTemplatesRSAA = (dealerId: number, template: DataToRemoveTemplate, bffEndpointConfig: IConfigObj) => {
    const proxyUrl = buildFromConfig(bffEndpointConfig, '');
    const dealerIdEscaped = encodeURIComponent(dealerId);
    const templateIdEscaped = encodeURIComponent(template.id);
    const endpoint = `${proxyUrl}/dealer/${dealerIdEscaped}/deal-templates/${templateIdEscaped}`;

    return {
        [RSAA]: {
            endpoint,
            method: 'DELETE',
            headers: { 'Content-Type': 'application/json' },
            types: [
                {
                    type: DELETE_DEAL_TEMPLATE_REQUEST,
                    meta: { template }
                },
                DELETE_DEAL_TEMPLATE_SUCCESS,
                DELETE_DEAL_TEMPLATE_FAILURE
            ]
        } satisfies RSAACallRequest
    };
};

type FullDataToUpdateTemplate = DataToUpdateTemplate & { updatedByName: string; updatedBy: string };

export const updateTemplateRSAA = (
    dealerId: number,
    fullTemplate: FullDataToUpdateTemplate,
    bffEndpointConfig: IConfigObj,
    meta: { name: string; id: string; inventoryType?: DealTemplateInventoryTypeExtended }
) => {
    const { forceAutoApply, id, ...template } = fullTemplate;
    const proxyUrl = buildFromConfig(bffEndpointConfig, '');
    const dealerIdEscaped = encodeURIComponent(dealerId);
    const templateIdEscaped = encodeURIComponent(id);

    const queryString = forceAutoApply ? `?forceAutoApply=true` : '';
    const endpoint = `${proxyUrl}/dealer/${dealerIdEscaped}/deal-templates/${templateIdEscaped}${queryString}`;

    return {
        [RSAA]: {
            endpoint,
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(template),
            types: [
                {
                    type: UPDATE_DEAL_TEMPLATE_REQUEST,
                    meta
                },
                UPDATE_DEAL_TEMPLATE_SUCCESS,
                UPDATE_DEAL_TEMPLATE_FAILURE
            ]
        } satisfies RSAACallRequest
    };
};

export const manuallyApplyDealTemplate = (templateId: string, templateName: string, source?: ManualTemplateApplySource) => {
    return {
        type: MANUALLY_APPLY_DEAL_TEMPLATE_REQUEST,
        payload: { templateId, templateName, ...(source && { source }) }
    };
};

export const revertManuallyAppliedTemplate = () => {
    return {
        type: REVERT_MANUALLY_APPLIED_TEMPLATE
    };
};

export const applyTemplateFromDealRSAA = (templateId: string) => (dispatch: (action: any) => any, getState: () => StateTree) => {
    const state = getState();
    const payload = buildApplyTemplateFromDealPayload(state, templateId);

    if (!payload) {
        dispatch({ type: INIT_TEMPLATE_FROM_DEAL_FAILURE, error: true, payload: new Error('Failed to build payload') });
        return;
    }

    const locHref = locHrefUtil.getLocHref();
    const bffApiUrlResult = apiConfigHelper.buildBffApiUrl(MANAGER_VIEW_APP_PREFIX, locHref, 'offer/apply-template-from-deal');
    const endpoint = bffApiUrlResult.url;

    return dispatch({
        [RSAA]: {
            endpoint,
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(payload),
            types: [INIT_TEMPLATE_FROM_DEAL_REQUEST, INIT_TEMPLATE_FROM_DEAL_SUCCESS, INIT_TEMPLATE_FROM_DEAL_FAILURE]
        } satisfies RSAACallRequest
    });
};
